/**
 * Market Regime Widget — flat, minimal
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Skeleton, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tradingDecisionAPI } from '../../services/api';

const R_CLR = { ALLOW: '#10b981', WATCH: '#f59e0b', AVOID: '#ef4444' };
const riskClr = v => v <= 30 ? '#10b981' : v <= 70 ? '#f59e0b' : '#ef4444';

const MarketRegimeWidget = ({ calcDate = null, onRegimeChange = null }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => { fetchRegime(); }, [calcDate]); // eslint-disable-line

  const fetchRegime = async () => {
    try {
      setLoading(true); setError(null);
      const r = await tradingDecisionAPI.getRegime(calcDate);
      setData(r.data);
      if (onRegimeChange) onRegimeChange(r.data.regime);
    } catch (err) { setError(err.message || 'Failed'); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
            <Skeleton width={90} height={14} animation="wave" />
            <Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: 0.5 }} animation="wave" />
          </Box>
          <Skeleton width={50} height={22} animation="wave" />
        </Box>
        <Box sx={{ px: 1.5, py: 0.6 }}><Skeleton width="80%" height={14} animation="wave" /></Box>
        <Box sx={{ px: 1.5, py: 0.5 }}>
          {[0,1,2,3].map(i => <Skeleton key={i} height={20} sx={{ mb: 0.3 }} animation="wave" />)}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.5 }}>
        <Typography sx={{ fontSize: 11, color: '#ef4444' }}>{error}</Typography>
      </Box>
    );
  }

  if (!data) return null;

  const regime = data.regime || 'WATCH';
  const c = R_CLR[regime] || '#64748b';
  const risk = data.macro_risk || 0;

  const comps = [
    { label: t('tradingDecision.macroRisk.macroRisk', 'Macro Risk'), score: risk },
    { label: t('tradingDecision.macroRisk.marketTrend', 'Market Trend'), score: Math.min(100, Math.max(0, 100 - risk + 20)) },
    { label: t('tradingDecision.macroRisk.volatility', 'Volatility'), score: Math.min(100, risk * 1.2) },
    { label: t('tradingDecision.macroRisk.sentiment', 'Sentiment'), score: Math.min(100, Math.max(0, 100 - risk * 0.8)) },
  ];

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('tradingDecision.macroRisk.title', 'Macro Risk')}</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: c, px: 0.6, py: 0.15, borderRadius: 0.5, bgcolor: alpha(c, 0.1) }}>
            {t(`tradingDecision.regime.${regime.toLowerCase()}.label`, regime)}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 900, color: c, fontFamily: 'monospace', lineHeight: 1 }}>
          {risk.toFixed(0)}<span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>/100</span>
        </Typography>
      </Box>

      {/* Description */}
      <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid #f8fafc' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.3 }}>
          {t(`tradingDecision.regime.${regime.toLowerCase()}.description`,
            regime === 'ALLOW' ? 'Market favorable for new positions' :
            regime === 'WATCH' ? 'Monitor market conditions carefully' : 'Avoid new positions')}
        </Typography>
      </Box>

      {/* Sub-scores as flat rows */}
      <Box sx={{ px: 1.5, py: 0.5 }}>
        {comps.map(comp => (
          <Box key={comp.label} sx={{ display: 'flex', alignItems: 'center', py: 0.35 }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', width: 80, flexShrink: 0 }}>{comp.label}</Typography>
            <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#f1f5f9', mx: 1 }}>
              <Box sx={{ height: '100%', borderRadius: 2, bgcolor: riskClr(comp.score), width: `${Math.min(comp.score, 100)}%` }} />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: riskClr(comp.score), fontFamily: 'monospace', minWidth: 24, textAlign: 'right' }}>{comp.score.toFixed(0)}</Typography>
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
        <Typography sx={{ fontSize: 10, color: '#b0b8c4' }}>
          {data.date ? new Date(data.date).toLocaleDateString('vi-VN') : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default MarketRegimeWidget;
